/* @flow */
import moment from 'moment';
import * as React from 'react';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';

import CaaSForm from './CaaSForm';
import Form from 'forms/_.extend';
import CaaSFormError from './CaaSFormError';
import CaaSFormSuccess from './CaaSFormSuccess';
import Loading from 'components/Loading/Loading';
import { actionDrawerStore } from 'services/ServiceRegistry';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
import { CAAS_ACTIONS, ADDRESS_OPTS, CONTACT_OPTS } from './constants';
import CaaSAssetCollectionDetails from 'utils/CaaSAssetCollectionDetails';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: Object,
};

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class CaaSDetails extends React.Component<*> {
  componentWillUnmount() {
    this.props.assetStore.clearDetails();
  }

  createForm = () => {
    const { t, assetStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;
    const activeUser: Object = JSON.parse(localStorage.getItem('user'));
    const caasAssetCollectionDetails = new CaaSAssetCollectionDetails(assetStore);
    const contactDetails = caasAssetCollectionDetails.getContactDetails();

    // All CaaS Actions must be visible for System Admin or Asset Manager roles.
    const isCaaSAdminRole = activeUser.role_id === 9 || activeUser.role_id === 7;

    const fields: Array<Object> = [
      {
        name: 'caasDetails',
        label: 'CaaS Details',
        fields: [
          {
            name: 'workOrderNumber',
            label: 'Work Order Number',
            rules: 'string|min:8|max:32',
            value: null,
          },
          {
            name: 'collectionAddress',
            label: 'Collection Address',
            rules: 'required',
            value: caasAssetCollectionDetails.isUsingCustomAddress()
              ? ADDRESS_OPTS.newAddress
              : ADDRESS_OPTS.currentAddress,
            options: [
              {
                id: ADDRESS_OPTS.currentAddress,
                label: 'Use Asset Location Address',
                value: ADDRESS_OPTS.currentAddress,
              },
              {
                id: ADDRESS_OPTS.newAddress,
                label: 'Custom Address',
                value: ADDRESS_OPTS.newAddress,
              },
            ],
          },
          {
            name: 'collection_address',
            label: 'Address',
            rules: `string|required_if:caasDetails.collectionAddress,${ADDRESS_OPTS.newAddress}`,
            value: caasAssetCollectionDetails.getAddress(),
          },
          {
            name: 'collectionDate',
            label: t('general:assets.labels.date_available_for_collection'),
            rules: `required|date`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'completionDate',
            label: 'Close Date',
            rules: `required|date`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'contactDetails',
            label: 'Contact Details',
            rules: 'required',
            value: caasAssetCollectionDetails.isUsingCustomContactDetails()
              ? CONTACT_OPTS.newContact
              : CONTACT_OPTS.currentContact,
            options: [
              {
                id: CONTACT_OPTS.currentContact,
                label: 'Use Asset Contact',
                value: CONTACT_OPTS.currentContact,
              },
              {
                id: CONTACT_OPTS.newContact,
                label: 'Custom Contact',
                value: CONTACT_OPTS.newContact,
              },
            ],
          },
          {
            name: 'contact_name',
            label: 'Name',
            rules: `required_if:caasDetails.contactDetails,${CONTACT_OPTS.newContact}`,
            value: contactDetails.name,
          },
          {
            name: 'contact_phone',
            label: 'Phone',
            rules: `telephone|required_if:caasDetails.contactDetails,${CONTACT_OPTS.newContact}`,
            value: contactDetails.phone,
          },
        ],
      },
    ];

    if (isCaaSAdminRole) {
      fields[0].fields.push({
        name: 'caas_actions',
        label: 'CaaS Actions',
        value: CAAS_ACTIONS.caas_schedule.name,
        options: [
          { id: CAAS_ACTIONS.caas_schedule.id, name: CAAS_ACTIONS.caas_schedule.name },
          { id: CAAS_ACTIONS.caas_updates.id, name: CAAS_ACTIONS.caas_updates.name },
          { id: CAAS_ACTIONS.caas_completion.id, name: CAAS_ACTIONS.caas_completion.name },
        ],
      });
    } else {
      fields[0].fields.push({
        name: 'caas_actions',
        label: 'CaaS Actions',
        value: CAAS_ACTIONS.caas_schedule.name,
        options: [{ id: CAAS_ACTIONS.caas_schedule.id, name: CAAS_ACTIONS.caas_schedule.name }],
      });
    }

    const hooks: Object = {
      onInit(data: Object) {
        if (!assetStore.asset) {
          assetStore.clearDetails();
          assetStore.loadAsset(asset.id);
        }
      },
      onSuccess(data: Object) {
        const formData = data.values().caasDetails;

        if (formData.caas_actions === CAAS_ACTIONS.caas_schedule.name) {
          const useAssetContact = formData.contactDetails === CONTACT_OPTS.currentContact;
          const useAssetCollectionAddress =
            formData.collectionAddress === ADDRESS_OPTS.currentAddress;

          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_schedule.id, {
            assets_ids: [asset.id],
            use_asset_contact: useAssetContact,
            use_asset_collection_address: useAssetCollectionAddress,
            contact_name: useAssetContact ? '' : formData.contact_name,
            contact_phone: useAssetContact ? '' : formData.contact_phone,
            collection_date: moment(formData.collectionDate).format('YYYY-MM-DD'),
            collection_address: useAssetCollectionAddress ? '' : formData.collection_address,
          });
        }

        if (formData.caas_actions === CAAS_ACTIONS.caas_updates.name) {
          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_updates.id, {
            assets_ids: [asset.id],
            work_order_number: formData.workOrderNumber,
          });
        }

        if (formData.caas_actions === CAAS_ACTIONS.caas_completion.name) {
          actionDrawerStore.requestCaaSActions(CAAS_ACTIONS.caas_completion.id, {
            assets_ids: [asset.id],
            closure_date: moment(formData.completionDate).format('YYYY-MM-DD'),
          });
        }

        assetStore.clearDetails();
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'CaaSDetails',
      }
    );
  };

  render(): React.Element<*> {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;
    const title: string = 'Calibration as a Service';

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <CaaSFormError />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <CaaSFormSuccess />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <CaaSForm renderMultiple={false} asset={asset} form={this.createForm()} />
      </div>
    );
  }
}
